/** @jsx jsx */
import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { getEnvConfig, getPermitActions, getApiTargetInfo } from 'core'
import { connect } from 'react-redux'
import { rowHeaderRender, hasAccesPermits } from 'core/utils'
// import * as ScreenPermissions from 'core/EventActions'
import { jsx } from '@emotion/core'
import { ErrorContext, SpinContext } from 'context'
import Grid, {
  virtualizedGridRenderer,
  freeEditEngine,
} from '@integec/grid-tools'
import { Button, message, Tabs, Upload, Icon, Select } from 'antd'
import { getAuthorization, getCredentials, getWind2NercRegion } from 'api/pgads'
import {
  Content,
  CriteriaRow,
  Filler,
  FullHeightSizer,
  Title,
} from 'core/screen-util'
import {
  JcTmpErrorTable,
  isErrorContains,
} from 'core/component/JcTmpErrorDialog'
import {
  // Wind2NercEventHeaders,
  Wind2PerformanceIoHeaders,
} from 'wind2/Headers'
import {
  Wind2NercEventHeadersImport,
  Wind2SubgroupHeadersImport,
  Wind2PlantHeaders,
  Wind2StorageGroupHeaders,
  Wind2StoragePerformanceIoHeaders,
} from './windHeader'
import {
  resubmitWind2EnergyStorage,
  resubmitWind2Event,
  resubmitWind2Plant,
  resubmitWind2StoragePerformance,
  resubmitWind2SubGroup,
  resubmitWind2SubGroupPerf,
} from 'wind2/wind2api'
import { strCol } from '@integec/grid-tools/lib/cols'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import { YNBooleanTypes } from 'core/HeaderColTypeProps'
import * as ScreenPermissions from 'core/EventActions'
import moment from 'moment'
import R from 'ramda'
import { getSystemConfigurationInfo } from '../../api/pgads'

const PLANT = 'plant'
const SUB_GROUP = 'sub_group'
const EVENT = 'event'
const EVENT_FLAT = 'event_flat'
const SUB_GROUP_PERFORMANCE = 'sub_group_performance'
const STORAGE_GROUP = 'storage_group'
const STORAGE_PERFORMANCE = 'storage_performance'

const Wind2PerformanceIoHeadersWithResubmit = [
  strCol({
    ident: 'included',
    display: 'Included',
    ...toSelectionColProps(YNBooleanTypes),
  }),
  ...Wind2PerformanceIoHeaders,
]

const Wind2SubgroupHeadersImportWithResubmit = [
  strCol({
    ident: 'included',
    display: 'Included',
    ...toSelectionColProps(YNBooleanTypes),
  }),
  ...Wind2SubgroupHeadersImport,
]

const Wind2NercEventHeadersImportWithResubmit = [
  strCol({
    ident: 'included',
    display: 'Included',
    ...toSelectionColProps(YNBooleanTypes),
  }),
  ...Wind2NercEventHeadersImport(1000, []),
]

const Wind2PlantHeadersWithResubmit = regionMap => [
  strCol({
    ident: 'included',
    display: 'Included',
    ...toSelectionColProps(YNBooleanTypes),
  }),
  ...Wind2PlantHeaders(regionMap),
]

const Wind2StorageGroupHeadersWithResubmit = [
  strCol({
    ident: 'included',
    display: 'Included',
    ...toSelectionColProps(YNBooleanTypes),
  }),
  ...Wind2StorageGroupHeaders,
]

const Wind2StoragePerformanceIoHeadersWithResubmit = [
  strCol({
    ident: 'included',
    display: 'Included',
    ...toSelectionColProps(YNBooleanTypes),
  }),
  ...Wind2StoragePerformanceIoHeaders,
]

const Wind2FileImport = ({ apiTargetInfo, envConfig, actionPermits }) => {
  const [importResult, setImportResult] = useState({
    jcTmpErrors: [],
    data: [],
  })
  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )
  const [activeKey, setActiveKey] = useState('data')
  const [selectedType, setSelectedType] = useState()

  const { setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [fuzzyFilter] = useState([])
  const [regionMap, setRegionMap] = useState({})
  const [fileName, setFileName] = useState('')

  const [isAllowed, setIsAllowed] = useState({ event: false, perf: false })

  useEffect(
    _ => {
      getSystemConfigurationInfo(apiTargetInfo).fork(setError, data => {
        const event = data.find(
          d => d.key === 'WIND2_IMPORT_XML_NERC_EVENT'
        ) || { value: 'NO' }
        const perf = data.find(
          d => d.key === 'WIND2_IMPORT_XML_NERC_PERFORMANCE'
        ) || { value: 'NO' }
        const val = {
          event: event.value === 'YES',
          perf: perf.value === 'YES',
        }
        if (val.event) {
          setSelectedType(EVENT)
        } else if (val.perf) {
          setSelectedType(SUB_GROUP_PERFORMANCE)
        } else setSelectedType(SUB_GROUP)
        setIsAllowed(val)
      })
    },
    [apiTargetInfo, setError]
  )

  const changePermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode: envConfig.authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.CHANGE_PERMISSIONS],
        matchAny: true,
      }),
    [actionPermits, envConfig.authGodMode]
  )

  const TYPEINFO_MAP = useMemo(
    _ => {
      const { event, perf } = isAllowed
      const maprow = {
        [SUB_GROUP_PERFORMANCE]: perf && {
          displayLabel: 'Subgroup Performance',
          dataHeaders: Wind2PerformanceIoHeadersWithResubmit.filter(
            f => f.ident !== 'subgroupName'
          ).map(d => ({ ...d, ellipsis: true })),
          importUrl: 'wind2-file-creation/subgroup-perf/import',
          resubmitApi: resubmitWind2SubGroupPerf,
          order: 2,
        },
        [SUB_GROUP]: {
          displayLabel: 'Subgroup',
          dataHeaders: Wind2SubgroupHeadersImportWithResubmit.map(d => ({
            ...d,
            ellipsis: true,
          })),
          importUrl: 'wind2-file-creation/subgroup/import',
          resubmitApi: resubmitWind2SubGroup,
          order: 5,
        },
        [EVENT]: event && {
          displayLabel: 'Events',
          dataHeaders: Wind2NercEventHeadersImportWithResubmit.map(d => ({
            ...d,
            ellipsis: true,
          })),
          importUrl: 'wind2-file-creation/nerc-event/import',
          resubmitApi: resubmitWind2Event,
          order: 1,
        },
        [EVENT_FLAT]: event && {
          displayLabel: 'Events (Generated from NERC-Excel)',
          dataHeaders: Wind2NercEventHeadersImportWithResubmit.map(d => ({
            ...d,
            ellipsis: true,
          })),
          importUrl: 'wind2-file-creation/nerc-event-flat/import',
          resubmitApi: resubmitWind2Event,
          order: 1,
        },
        [PLANT]: {
          displayLabel: 'Plant',
          dataHeaders: Wind2PlantHeadersWithResubmit(regionMap).map(d => ({
            ...d,
            ellipsis: true,
          })),
          importUrl: 'wind2-file-creation/plant/import',
          resubmitApi: resubmitWind2Plant,
          order: 4,
        },
        [STORAGE_GROUP]: {
          displayLabel: 'Storage Group',
          dataHeaders: Wind2StorageGroupHeadersWithResubmit.map(d => ({
            ...d,
            ellipsis: true,
          })),
          importUrl: 'wind2-file-creation/storage-group/import',
          resubmitApi: resubmitWind2EnergyStorage,
          order: 6,
        },
        [STORAGE_PERFORMANCE]: {
          displayLabel: 'Storage Performance',
          dataHeaders: Wind2StoragePerformanceIoHeadersWithResubmit.map(d => ({
            ...d,
            ellipsis: true,
          })),
          importUrl: 'wind2-file-creation/storage-performance/import',
          resubmitApi: resubmitWind2StoragePerformance,
          order: 3,
        },
      }

      const mv = R.compose(
        R.fromPairs,
        R.filter(([_, v]) => v),
        R.toPairs
      )(maprow)

      return mv
    },
    [regionMap, isAllowed]
  )

  const onChange = () => {
    setImportResult({
      jcTmpErrors: [],
      data: [],
    })
    setFileName('')
    setEditInfo(freeEditEngine.generateInitialEditInfo())
  }

  useEffect(
    _ => {
      getWind2NercRegion(apiTargetInfo).fork(setError, data => {
        const avaStatus = R.reduce(
          (a, b) => ({ ...a, ...b }),
          {},
          data.map(d => ({
            [d.regionCode]: d.regionCode + ' - ' + d.regionName,
          }))
        )
        setRegionMap(avaStatus)
      })
    },
    [apiTargetInfo, setError]
  )

  const getFileProps = useCallback(() => {
    const { importUrl } = TYPEINFO_MAP[selectedType] || {}

    return {
      name: 'file',
      action: `${apiTargetInfo.baseUrl}/${importUrl}`,
      method: 'POST',
      headers: getAuthorization(apiTargetInfo),
      ...getCredentials(apiTargetInfo),
    }
  }, [TYPEINFO_MAP, apiTargetInfo, selectedType])

  const setResponse = res => {
    // eslint-disable-next-line no-unused-vars
    const { jcTmpErrors, data } = res
    setActiveKey('errors')
    if (isErrorContains(jcTmpErrors)) {
      setImportResult({
        jcTmpErrors,
        data: data.map(d => ({ ...d, included: 'Y' })),
      })
    } else {
      setImportResult({ jcTmpErrors, data: [] })
      message.success(`Data submited successfully.`)
      setFileName('')
    }
  }

  const onFileUploadChange = useCallback(
    info => {
      setFileName(info.file.name)
      if (info.file.status === 'done') {
        setSpinning(false)
        setResponse(info.file.response)
      } else if (info.file.status === 'error') {
        setSpinning(false)
        setError(info.file.response)
        if (info.file.response.appExceptionId !== 100)
          message.error(`${info.file.name} file have invalid data`)
      }
    },
    [setError, setSpinning]
  )

  const isEditableComp = ({ header }) => {
    if (header.ident === 'modifier' || header.ident === 'modifiedDate')
      return false

    return true
  }

  const editRow = editInfo => {
    const modData = importResult.data.map(d => {
      if (editInfo.updatedMap.get(d)) {
        return {
          ...editInfo.updatedMap.get(d),
          modificationInfo: undefined,
          updated: true,
        }
      } else {
        return d
      }
    })

    setImportResult(p => ({ ...p, data: modData }))
    setEditInfo(editInfo)
  }

  const onReSubmitData = ({ data }) => {
    const { resubmitApi } = TYPEINFO_MAP[selectedType]

    setSpinning(true)
    let mdata = data.filter(d => d.included === 'Y')
    if (selectedType === SUB_GROUP) {
      mdata = data
        .filter(d => d.included === 'Y')
        .map(d => ({
          ...d,
          commissionDate: d.commissionDateStr
            ? moment(d.commissionDateStr, 'MM/DD/YYYY').format('YYYY-MM-DD')
            : undefined,
          effectiveDate: d.effectiveDateStr
            ? moment(d.effectiveDateStr, 'MM/DD/YYYY').format('YYYY-MM-DD')
            : undefined,
          isInsert: true,
        }))
    } else if (selectedType === PLANT) {
      mdata = mdata
        .filter(d => d.included === 'Y')
        .map(d => ({
          ...d,
          effectiveDate: d.effectiveDateStr
            ? moment(d.effectiveDateStr, 'MM/DD/YYYY').format('YYYY-MM-DD')
            : undefined,
          isInsert: true,
        }))
    } else if (selectedType === STORAGE_GROUP) {
      mdata = data
        .filter(d => d.included === 'Y')
        .map(d => ({
          ...d,
          commisionDate: d.commissionDateStr,
          // ? moment(d.commissionDateStr, 'MM/DD/YYYY').format('YYYY-MM-DD')
          // : undefined,
          effectiveDate: d.effectiveDateStr,
          // ? moment(d.effectiveDateStr, 'MM/DD/YYYY').format('YYYY-MM-DD')
          // : undefined,
          isInsert: true,
        }))
    }
    if (mdata.length <= 0) {
      setSpinning(false)
      message.error('Please select data to re-submit')
    } else {
      resubmitApi(apiTargetInfo)(mdata).fork(setError, res => {
        // setImportResult(res)
        setResponse(res)
        // setActiveKey('errors')
        setEditInfo(freeEditEngine.generateInitialEditInfo())
        setSpinning(false)
      })
    }
  }

  return (
    <div
      css={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <CriteriaRow>
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
            width: '250px',
          }}
        >
          Wind NERC File Import
        </Title>
        <Select
          value={selectedType}
          style={{ width: '300px' }}
          onChange={plantId => {
            setSelectedType(plantId)
            onChange()
          }}
        >
          {// Object.keys(TYPEINFO_MAP)
          [
            isAllowed.event && EVENT,
            isAllowed.event && EVENT_FLAT,
            isAllowed.perf && SUB_GROUP_PERFORMANCE,
            STORAGE_PERFORMANCE,
            changePermission && PLANT,
            changePermission && SUB_GROUP,
            changePermission && STORAGE_GROUP,
          ]
            .filter(f => f)
            .map(k => (
              <Select.Option key={k} value={k}>
                {TYPEINFO_MAP[k].displayLabel}
              </Select.Option>
            ))}
        </Select>

        <Upload
          {...getFileProps()}
          onChange={data => {
            setSpinning(true)
            onFileUploadChange(data)
          }}
          multiple={false}
          showUploadList={false}
        >
          <Button style={{ marginLeft: 10 }} type="primary">
            <Icon type="upload" /> Import XML
          </Button>
        </Upload>
        <Filler />
        <div>
          Last File Imported : <b>{fileName}</b>
        </div>
        <Filler />
        <Button
          // loading={loading}
          style={{ marginLeft: '10px' }}
          disabled={editInfo.updated.length <= 0}
          onClick={() => onReSubmitData({ data: importResult.data })}
          icon="edit"
          type="danger"
        >
          Re-Submit
        </Button>
        <Filler />
        {/* <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          style={{ marginLeft: '10px', marginRight: '10px', maxWidth: '200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        /> */}
      </CriteriaRow>
      <FullHeightSizer heightOffset={240}>
        {({ height, width }) => (
          <Content css={{ height: height, paddingTop: '10px' }}>
            <Tabs
              activeKey={activeKey}
              onChange={setActiveKey}
              type="card"
              style={{ width: width - 10 }}
            >
              <Tabs.TabPane key="data" tab="Data">
                <div style={{ height, width }}>
                  <Grid
                    sortEnabled
                    data={importResult.data}
                    headers={TYPEINFO_MAP[selectedType]?.dataHeaders || []}
                    altBgColor="#ebebeb"
                    render={virtualizedGridRenderer({
                      autoFixColByKey: false,
                      rowHeaderRender,
                      height: height - 80,
                      width: width - 80,
                    })}
                    onEditInfoChange={editInfo => editRow(editInfo)}
                    editInfo={editInfo}
                    editMode="cell"
                    isEditable={isEditableComp}
                    fuzzyFilter={fuzzyFilter}
                    selectionMode="single"
                    selectionType="row"
                  />
                </div>
              </Tabs.TabPane>
              <Tabs.TabPane key="errors" tab="Errors">
                <JcTmpErrorTable
                  errors={importResult.jcTmpErrors}
                  scroll={{ y: height - 80 }}
                  style={{ width: width - 30 }}
                />
              </Tabs.TabPane>
            </Tabs>
          </Content>
        )}
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  envConfig: getEnvConfig(state),
  actionPermits: getPermitActions(state),
  apiTargetInfo: getApiTargetInfo(state),
})
export default connect(mapStateToProps)(Wind2FileImport)
